import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import foodDatabase from './foodDatabase';
import mixpanel from 'mixpanel-browser';
import './DefaultPage.css';

// Initialize Mixpanel
mixpanel.init('8571c03c2c1e9c28eb8c3cd4b9da6947', { ignore_dnt: true });

const DefaultPage = () => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [inputError, setInputError] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        mixpanel.track('Page Viewed', {
            timestamp: new Date().toISOString(),
            referrer: document.referrer
        });
    }, []);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        mixpanel.track('Input Typed', {
            current_value: value,
            suggestions_count: foodDatabase.filter((food) =>
                food.toLowerCase().includes(value.toLowerCase())
            ).length
        });

        if (value.trim() === '') {
            setSuggestions([]);
        } else {
            const filteredSuggestions = foodDatabase
                .filter((food) => food.toLowerCase().includes(value.toLowerCase()))
                .slice(0, 10);
            setSuggestions(filteredSuggestions);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setInputValue(suggestion);
        setSuggestions([]);
        mixpanel.track('Suggestion Clicked', { suggestion });
    };

    const handleSearch = async () => {
        if (!inputValue.trim()) {
            setInputError(true);
            mixpanel.track('Submit Empty Form', { timestamp: new Date().toISOString() });
            return;
        }

        setInputError(false);
        setErrorMessage('');
        setIsLoading(true);

        mixpanel.track('Search Initiated', {
            dish: inputValue,
            timestamp: new Date().toISOString()
        });

        const startTime = Date.now();
        try {
            const response = await fetch(
                `https://api.winetodish.com/dish/${encodeURIComponent(inputValue)}`
            );
            if (!response.ok) {
                throw new Error('Failed to fetch wine pairing data.');
            }

            const data = await response.json();
            const responseTime = Date.now() - startTime;
            mixpanel.track('Search Completed', {
                dish: inputValue,
                response_time: responseTime,
                wine_pairing_found: !!data
            });
            navigate(`/dish/${encodeURIComponent(inputValue)}`, { state: { wineResult: data } });
        } catch (error) {
            mixpanel.track('Search Failed', { dish: inputValue, error_message: error.message });
            setErrorMessage('An error occurred while fetching the wine pairing. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // 🟢 Add JSON-LD Schema for SEO (unchanged logic, only new structured data)
    const homeSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "WineToDish",
        "url": "https://winetodish.com",
        "potentialAction": {
            "@type": "SearchAction",
            "target": "https://winetodish.com/dish/{search_term_string}",
            "query-input": "required name=search_term_string"
        }
    };

    return (
        <div className="container-fluid">
	    <Helmet>
	        <title>Find the Perfect Wine Pairing | WineToDish</title>
		    <meta
			name="description"
			content="Discover the perfect wine for your favorite dishes with expert pairing recommendations."
		    />
		    <link rel="canonical" href="https://winetodish.com/" />
		    <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>
 	    </Helmet>

            <div className="search-container">
                <h1>Find Your Perfect Wine Pairing</h1>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSearch();
                    }}
                >
                    <div className="mb-3 position-relative">
                        <input
                            type="text"
                            className={`form-control ${inputError ? 'is-invalid' : ''}`}
                            id="dish-search"
                            placeholder="Search your dish (e.g., pasta bolognese)"
                            autoComplete="off"
                            value={inputValue}
                            onChange={handleInputChange}
                        />
                        {inputError && <div className="invalid-feedback">Please enter a dish.</div>}
                        {suggestions.length > 0 && (
                            <ul className="list-group position-absolute w-100">
                                {suggestions.map((suggestion, index) => (
                                    <li
                                        key={index}
                                        className="list-group-item"
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {suggestion}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Loading...
                            </>
                        ) : (
                            'Find Wine'
                        )}
                    </button>
                </form>
                {errorMessage && <div className="text-danger mt-2">{errorMessage}</div>}
<a
    className="new-search-link"
    href="https://www.winetodish.com/blog"
    onClick={(e) => {
        e.preventDefault(); // Prevent full reload if JavaScript is enabled
        navigate('/');
    }}
>
    See our wine pairing blog
</a>

            </div>
        </div>
    );
};

export default DefaultPage;
